import { makeAutoObservable, runInAction } from 'mobx';

import { saveStripeSubscription as saveStripeSubscriptionAPI } from './api';
import {
  logError,
  tryGetFirstError,
} from '../../../shared/helpers/functions/api';
import {
  AreaUnit,
  StripePlan,
  StripeSubscriptionItemInput,
} from '../../../shared/graphql/graphql';
import type { IRootStore } from '../rootStore/RootStore';
import { Plan } from '../../../shared/helpers/constants/plans';

export default class CheckoutStore {
  rootStore: IRootStore;

  promocode = '';

  subscription: {
    clientSecret: string | null;
    id: string | null;
    inProgress: boolean;
    error: Error | null;
  } = {
    clientSecret: null,
    id: null,
    inProgress: false,
    error: null,
  };

  paymentInProgress = false;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setPromocode(promocode: string) {
    this.promocode = promocode;
  }

  setPaymentInProgress(value: boolean) {
    this.paymentInProgress = value;
  }

  reset() {
    runInAction(() => {
      this.promocode = '';
      this.subscription = {
        clientSecret: null,
        id: null,
        inProgress: false,
        error: null,
      };
      this.paymentInProgress = false;
    });
  }

  async saveStripeSubscription({
    plan,
    customerId,
    items,
    promocode,
    areaUnit,
  }: {
    plan: Plan.monthly | Plan.annual;
    customerId: string;
    items: StripeSubscriptionItemInput[];
    promocode?: string;
    areaUnit: AreaUnit;
  }) {
    this.subscription.inProgress = true;
    this.subscription.error = null;

    try {
      let stripePlan;

      if (plan === Plan.monthly) {
        stripePlan = StripePlan.MonthlyPayAsYouGo;
      } else if (plan === Plan.annual) {
        stripePlan = StripePlan.AnnualSubscription;
      } else {
        throw new Error(
          '[CheckoutStore][saveStripeSubscription] Invalid plan.',
        );
      }

      const { data } = await saveStripeSubscriptionAPI({
        plan: stripePlan,
        promocode: promocode ?? null,
        customerId,
        subscriptionId: this.subscription.id,
        items,
        areaUnit,
      });

      if (!data?.saveStripeSubscription) {
        throw new Error(
          '[CheckoutStore][saveStripeSubscription] Data is empty.',
        );
      }

      runInAction(() => {
        this.subscription.clientSecret =
          data.saveStripeSubscription?.clientSecret ?? null;
        this.subscription.id =
          data.saveStripeSubscription?.subscriptionId ?? null;
      });

      return data.saveStripeSubscription;
    } catch (error) {
      const typedError = tryGetFirstError(error);
      runInAction(() => {
        this.subscription.error = typedError;
      });
      logError(typedError);

      return null;
    } finally {
      runInAction(() => {
        this.subscription.inProgress = false;
      });
    }
  }
}
